import { ExternalLink, Package, Paperclip } from "lucide-react";

import type { Column } from "@/components/shared/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {
  EXPENSE_CATEGORY_LABELS,
  EXPENSE_FUNDING_SOURCE_LABELS,
  EXPENSE_PAYMENT_METHOD_LABELS,
  EXPENSE_STATUS_LABELS,
  EXPENSE_VALIDATION_LABELS,
} from "./expense-labels";

import type { Expense } from "@artisancode/api-types";

export const expenseColumns: Column<Expense>[] = [
  {
    key: "date",
    label: "Tanggal",
    render: (e) => {
      const hasStockReceipt = e.items?.some((i) => i.itemType && i.itemId);
      return (
        <div className="flex flex-col">
          <div className="flex flex-wrap items-center gap-1.5">
            <span>{e.date}</span>
            {e.code && (
              <Badge
                variant="outline"
                className="font-mono text-[10px] px-1.5 py-0 bg-muted/40 text-foreground"
              >
                {e.code}
              </Badge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1 mt-0.5">
            <Badge variant="secondary" className="w-fit text-[10px]">
              {EXPENSE_CATEGORY_LABELS[e.category]}
            </Badge>
            {e.status === "draft" && (
              <Badge
                variant="outline"
                className="w-fit text-[10px] border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
              >
                {EXPENSE_STATUS_LABELS.draft}
              </Badge>
            )}
            {hasStockReceipt && (
              <Badge
                variant="outline"
                className="w-fit text-[10px] text-emerald-600 border-emerald-300 bg-emerald-50 dark:bg-emerald-950/40 gap-0.5"
              >
                <Package className="h-2.5 w-2.5" />
                Stok Masuk
              </Badge>
            )}
          </div>
        </div>
      );
    },
  },
  {
    key: "description",
    label: "Keterangan",
    render: (e) => {
      const hasMultipleItems = e.items && e.items.length > 1;
      return (
        <div className="flex flex-col">
          <span className="font-medium">{e.description || "-"}</span>
          <span className="text-xs text-muted-foreground">
            {hasMultipleItems
              ? `${e.items?.length ?? 0} item`
              : `${e.items?.[0]?.qtyInUom ?? e.qty}x @ ${e.pricePerUnit.toLocaleString("id-ID")}`}
          </span>
        </div>
      );
    },
  },
  {
    key: "amount",
    label: "Jumlah / Kas Keluar",
    render: (e) => {
      const hasDiscOrTax =
        (e.discountAmount !== undefined && e.discountAmount > 0) ||
        (e.taxAmount !== undefined && e.taxAmount > 0);

      return (
        <div className="flex flex-col">
          <span className="font-semibold text-foreground">
            Rp {e.amount.toLocaleString("id-ID")}
          </span>
          {hasDiscOrTax && (
            <span className="text-[10px] text-muted-foreground">
              {e.discountAmount && e.discountAmount > 0
                ? `Disc -Rp ${e.discountAmount.toLocaleString("id-ID")}`
                : ""}
              {e.discountAmount &&
              e.discountAmount > 0 &&
              e.taxAmount &&
              e.taxAmount > 0
                ? " | "
                : ""}
              {e.taxAmount && e.taxAmount > 0
                ? `Pajak +Rp ${e.taxAmount.toLocaleString("id-ID")}`
                : ""}
            </span>
          )}
          <span className="text-xs text-muted-foreground">
            Kas keluar: Rp {e.cashOut.toLocaleString("id-ID")}
          </span>
        </div>
      );
    },
  },
  {
    key: "payment",
    label: "Pembayaran",
    render: (e) => (
      <div className="flex flex-col gap-1">
        <span className="text-sm">
          {EXPENSE_PAYMENT_METHOD_LABELS[e.paymentMethod]}
        </span>
        <span className="text-xs text-muted-foreground">
          {EXPENSE_FUNDING_SOURCE_LABELS[e.fundingSource]}
        </span>
      </div>
    ),
  },
  {
    key: "receipts",
    label: "Bukti",
    render: (e) => {
      const urls =
        e.receiptUrls && e.receiptUrls.length > 0
          ? e.receiptUrls
          : e.receiptUrl
            ? [e.receiptUrl]
            : [];
      const count = e.receiptKeys?.length || (e.receiptKey ? 1 : 0);

      if (urls.length === 0 && count === 0) {
        return <span className="text-xs text-muted-foreground">-</span>;
      }

      if (urls.length === 1) {
        return (
          <a
            href={urls[0]}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-xs text-primary hover:underline font-medium"
          >
            <Paperclip className="h-3 w-3" />
            1 Berkas
            <ExternalLink className="h-2.5 w-2.5" />
          </a>
        );
      }

      if (urls.length > 1) {
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1 px-2 text-xs text-primary"
              >
                <Paperclip className="h-3 w-3" />
                {urls.length} Berkas
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              {urls.map((url, idx) => (
                <DropdownMenuItem key={idx} asChild>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-between cursor-pointer text-xs"
                  >
                    <span>Berkas #{idx + 1}</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      return (
        <Badge variant="secondary" className="text-[10px]">
          {count} Berkas
        </Badge>
      );
    },
  },
  {
    key: "validation",
    label: "Validasi",
    render: (e) => (
      <Badge variant={e.validation === "ok" ? "default" : "outline"}>
        {EXPENSE_VALIDATION_LABELS[e.validation]}
      </Badge>
    ),
  },
];
